import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { SpatialLayer, FeatureProps, GeoJSONFeatureCollection } from './types';

interface ClusteredLayerProps {
  layer: SpatialLayer;
  data: GeoJSONFeatureCollection;
  onFeatureClick: (feature: FeatureProps) => void;
}

export default function ClusteredLayer({ layer, data, onFeatureClick }: ClusteredLayerProps) {
  const map = useMap();
  const clusterRef = useRef<L.MarkerClusterGroup | null>(null);

  useEffect(() => {
    if (!data?.features?.length) return;

    const cluster = (L as any).markerClusterGroup({
      maxClusterRadius: 50,
      iconCreateFunction: (c: any) => {
        const count = c.getChildCount();
        return L.divIcon({
          html: `<div style="
            background:${layer.color};
            color:#fff;
            width:32px;height:32px;
            border-radius:50%;
            display:flex;align-items:center;justify-content:center;
            font-size:12px;font-weight:700;
            box-shadow:0 2px 6px rgba(0,0,0,0.25);
            border:2px solid #fff;
          ">${count}</div>`,
          className: '',
          iconSize: L.point(32, 32),
        });
      },
    });

    data.features.forEach((f) => {
      if (!f.geometry || f.geometry.type !== 'Point') return;
      const [lng, lat] = f.geometry.coordinates;
      const marker = L.circleMarker([lat, lng], {
        radius: 7,
        fillColor: layer.color,
        color: '#FFFFFF',
        weight: 2,
        fillOpacity: 0.85,
      });

      const props = f.properties || {};
      // Tooltip on hover
      marker.bindTooltip(
        `<strong>${props.name || 'Feature'}</strong><br/><span style="color:#747474;font-size:11px">${layer.name}</span>`,
        { direction: 'top', offset: L.point(0, -8) }
      );
      // Popup
      marker.bindPopup(
        `<div style="font-family:Roboto,Segoe UI,sans-serif;min-width:180px">` +
          `<div style="border-left:3px solid ${layer.color};padding-left:8px;margin-bottom:6px">` +
          `<div style="font-weight:700;font-size:14px">${props.name || 'Feature'}</div>` +
          `<div style="font-size:11px;color:#747474">${props.layer_name || ''}</div></div>` +
          `<div style="font-size:12px;color:#484848">${props.description || ''}</div>` +
          `</div>`
      );
      marker.on('click', () => onFeatureClick(props as FeatureProps));
      cluster.addLayer(marker);
    });

    clusterRef.current = cluster;
    map.addLayer(cluster);

    return () => {
      if (clusterRef.current) {
        map.removeLayer(clusterRef.current);
        clusterRef.current = null;
      }
    };
  }, [map, layer, data, onFeatureClick]);

  return null;
}
